import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { PortalLayout } from "../../components/portal/PortalLayout";
import { useBasket } from "../../context/BasketContext";
import { toast } from "sonner";
import { Trash2, Plus, Minus, ShoppingCart, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

export default function PortalBasket() {
  const { items, updateQty, remove, clear, subtotal } = useBasket();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [loading, setLoading] = useState(false);
  useEffect(() => { document.title = "My Order | Sambiva Trade"; }, []);

  const vat = subtotal * 0.23;
  const total = subtotal + vat;

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) };
      const { data } = await api.post("/trade/orders", payload);
      clear();
      qc.invalidateQueries();
      toast.success("Order successfully submitted.");
      navigate(`/portal/orders/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <PortalLayout title="My Order">
        <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 p-16 text-center" data-testid="basket-empty">
          <ShoppingCart className="mx-auto text-sambiva-muted mb-4" size={48} />
          <h3 className="font-serif text-2xl text-sambiva-charcoal mb-2">Your order is empty</h3>
          <p className="text-sambiva-muted mb-6">Add products from the trade catalogue to get started.</p>
          <button onClick={() => navigate("/portal/catalogue")} className="bg-sambiva-red text-white font-semibold px-6 py-3 rounded-full hover:bg-sambiva-deepred transition-colors">Browse Catalogue</button>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="My Order">
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-sambiva-cream text-sambiva-muted text-left">
              <tr><th className="px-4 py-3 font-semibold">Product</th><th className="px-4 py-3 font-semibold">SKU</th><th className="px-4 py-3 font-semibold">Case</th><th className="px-4 py-3 font-semibold">Price</th><th className="px-4 py-3 font-semibold">Qty</th><th className="px-4 py-3 font-semibold">Total</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.product_id} data-testid={`basket-row-${i.sku}`} className="border-t border-sambiva-charcoal/5">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3"><img src={i.image} alt="" className="w-11 h-11 rounded-lg object-cover" /><span className="font-medium text-sambiva-charcoal">{i.name}</span></div>
                  </td>
                  <td className="px-4 py-3 text-sambiva-muted">{i.sku}</td>
                  <td className="px-4 py-3 text-sambiva-charcoal">{i.case_quantity}</td>
                  <td className="px-4 py-3 text-sambiva-charcoal">€{i.trade_price.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center border border-sambiva-charcoal/15 rounded-lg w-fit">
                      <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="p-1.5"><Minus size={14} /></button>
                      <span className="w-8 text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="p-1.5"><Plus size={14} /></button>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-sambiva-charcoal">€{(i.trade_price * i.quantity).toFixed(2)}</td>
                  <td className="px-4 py-3"><button data-testid={`remove-${i.sku}`} onClick={() => remove(i.product_id)} className="text-sambiva-muted hover:text-sambiva-red"><Trash2 size={16} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 p-6 h-fit lg:sticky lg:top-24">
          <h3 className="font-serif text-xl text-sambiva-charcoal mb-4">Order Summary</h3>
          <div className="space-y-2 text-sm mb-5">
            <div className="flex justify-between"><span className="text-sambiva-muted">Subtotal</span><span className="text-sambiva-charcoal font-medium">€{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-sambiva-muted">VAT (23%)</span><span className="text-sambiva-charcoal font-medium">€{vat.toFixed(2)}</span></div>
            <div className="flex justify-between pt-2 border-t border-sambiva-charcoal/8 text-base"><span className="font-semibold text-sambiva-charcoal">Total</span><span className="font-bold text-sambiva-red" data-testid="basket-total">€{total.toFixed(2)}</span></div>
          </div>
          <button data-testid="submit-order" disabled={loading} onClick={submit} className="w-full bg-sambiva-green hover:bg-sambiva-deepgreen text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {loading && <Loader2 className="animate-spin" size={18} />} Submit Order
          </button>
        </div>
      </div>
    </PortalLayout>
  );
}
